/* eslint-disable lingui/no-unlocalized-strings -- contratos técnicos HTTP */
import type { StepUpGrant } from '@/shared/http';
import { creditApi } from '@/shared/http/client';
import { wire, withSignal } from '@/shared/reference/wire';
import {
  userCreatedSchema,
  userGroupPageSchema,
  userOptionPageSchema,
  userPageSchema,
  userSchema,
  type UserWrite,
} from './user.wire';
export async function listUserOptions(
  options: { tenant: string | undefined; name?: string | undefined },
  signal?: AbortSignal,
) {
  let url = '/User?size=10&page=0';
  if (options.tenant) url += `&tenant=${options.tenant}`;
  if (options.name) url += `&filter=name^="${options.name}"`;
  const data = await creditApi.get<unknown>(url, { routeTemplate: '/User', ...withSignal(signal) });
  return wire(userOptionPageSchema, data, 'GET /User');
}

export interface UserListParams {
  readonly page: number;
  readonly size: number;
  readonly tenant?: string | undefined;
  readonly name?: string | undefined;
  readonly email?: string | undefined;
  readonly paginationToken?: string | undefined;
}

/** A URL montada à mão do legado, na mesma ordem e sem codificar (services/user/index.ts:24-31). */
export function userListUrl({ page, size, tenant, name, email, paginationToken }: UserListParams) {
  let url = `/User?size=${size}&page=${page}`;
  url = tenant ? url + `&tenant=${tenant}` : url;
  url = name ? url + `&filter=name^="${name}"` : url;
  url = email ? url + `&filter=email^="${email}"` : url;
  url = paginationToken ? url + `&paginationToken=${paginationToken}` : url;
  return url;
}

export async function listUsers(params: UserListParams, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(userListUrl(params), {
    routeTemplate: '/User',
    ...withSignal(signal),
  });
  return wire(userPageSchema, data, 'GET /User');
}

// O id é o username do Cognito (e-mail): vai no caminho como veio, como o legado; o span usa o molde (U30).
export async function getUser(id: string, tenant: string | undefined, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/User/${id}`, {
    params: { tenant },
    routeTemplate: '/User/{id}',
    ...withSignal(signal),
  });
  return wire(userSchema, data, 'GET /User/{id}');
}

/** A autorização do step-up novo (`X-UY3-StepUp`), quando o endpoint a exige (`UsersController.cs`). */
const withStepUp = (stepUp: StepUpGrant | undefined) => (stepUp ? { stepUp } : {});

/** `[RequireStepUp(UserCreate)]` (`UsersController.cs:245-253`), sem registro. */
export async function createUser(body: UserWrite, tenant: string | undefined, stepUp?: StepUpGrant) {
  const data = await creditApi.post('/User', body, {
    params: { tenant },
    routeTemplate: '/User',
    ...withStepUp(stepUp),
  });
  return wire(userCreatedSchema, data, 'POST /User');
}

/** `[RequireStepUp(UserUpdate, ResourceRouteParam = "userId")]` (`UsersController.cs:286-294`). */
export async function updateUser(id: string, body: UserWrite, tenant: string | undefined, stepUp?: StepUpGrant) {
  return creditApi.put<unknown>(`/User/${id}`, body, {
    params: { tenant },
    routeTemplate: '/User/{id}',
    ...withStepUp(stepUp),
  });
}

/** `[RequireStepUp(UserCredentialReset, ResourceRouteParam = "id")]` (`UsersController.cs:432-439`). */
export async function disableUserMfa(id: string, tenant: string | undefined, stepUp?: StepUpGrant) {
  return creditApi.put<unknown>(
    `/User/${id}/DisableMFA`,
    {},
    { params: { tenant }, routeTemplate: '/User/{id}/DisableMFA', ...withStepUp(stepUp) },
  );
}

/** `[RequireStepUp(UserCredentialReset, ResourceRouteParam = "id")]` (`UsersController.cs:361-368`). */
export async function resetUserPassword(id: string, tenant: string | undefined, stepUp?: StepUpGrant) {
  return creditApi.put<unknown>(`/User/${id}/ResetPassword`, null, {
    params: { tenant },
    routeTemplate: '/User/{id}/ResetPassword',
    ...withStepUp(stepUp),
  });
}

export type EnableOrDisable = 'EnableUser' | 'DisableUser';

/** `[RequireStepUp(UserUpdate, ResourceRouteParam = "id")]` nos dois (`UsersController.cs:604-611`, `:638-645`). */
export async function enableOrDisableUser(
  id: string,
  type: EnableOrDisable,
  tenant: string | undefined,
  stepUp?: StepUpGrant,
) {
  return creditApi.put<unknown>(
    `/User/${id}/${type}`,
    {},
    { params: { tenant }, routeTemplate: `/User/{id}/${type}`, ...withStepUp(stepUp) },
  );
}

export interface UserGroupsParams {
  readonly page: number;
  readonly size: number;
  readonly paginationToken?: string | undefined;
  readonly tenant?: string | undefined;
}

/** `getUserGroupList` com `{ ...filtros, tenant }` (contexts/userContext.tsx:239, services/user/index.ts:121-127). */
export async function listUserGroups(id: string, params: UserGroupsParams, signal?: AbortSignal) {
  const data = await creditApi.get<unknown>(`/User/${id}/Groups`, {
    params: {
      page: params.page,
      size: params.size,
      paginationToken: params.paginationToken,
      tenant: params.tenant,
    },
    routeTemplate: '/User/{id}/Groups',
    ...withSignal(signal),
  });
  return wire(userGroupPageSchema, data, 'GET /User/{id}/Groups');
}

/** `[RequireStepUp(GroupMembership, ResourceRouteParam = "userId")]` (`UserGroupController.cs:262-270`): preso ao usuário. */
export async function insertUserInGroup(
  groupName: string,
  userId: string,
  tenant: string | undefined,
  stepUp?: StepUpGrant,
) {
  return creditApi.post(
    `/UserGroup/${groupName}/InsertUser/${userId}`,
    {},
    { params: { tenant }, routeTemplate: '/UserGroup/{groupName}/InsertUser/{id}', ...withStepUp(stepUp) },
  );
}

/** `[RequireStepUp(GroupMembership, ResourceRouteParam = "userId")]` (`UserGroupController.cs:298-306`). */
export async function removeUserFromGroup(
  groupName: string,
  userId: string,
  tenant: string | undefined,
  stepUp?: StepUpGrant,
) {
  return creditApi.delete<unknown>(`/UserGroup/${groupName}/RemoveUser/${userId}`, {
    params: { tenant },
    routeTemplate: '/UserGroup/{groupName}/RemoveUser/{id}',
    ...withStepUp(stepUp),
  });
}
